"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, CircleQuestionMark as HelpCircle, PhoneCall } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
const faqData = [{
  id: "item-1",
  question: "What is the typical timeline for a master bathroom renovation?",
  answer: "Most Brookhaven master bathroom renovations average 6 to 10 weeks of active construction. Ahead of demo, the architectural design and DeKalb County permitting phase takes 3 to 5 weeks. We secure all fixture orders, custom cabinetry, and slab selections before breaking ground so daily progress remains uninterrupted."
}, {
  id: "item-2",
  question: "How do you ensure shower waterproofing and prevent leaks?",
  answer: "We install complete bonded membrane assemblies using German-engineered Schluter Systems with uncoupling subfloors and integrated vapor barriers. Every custom shower pan undergoes a verified 24-hour standing water flood test before tile installation, backed by our structural craftsmanship standards."
}, {
  id: "item-3",
  question: "Which vanity countertop material performs best in humid baths?",
  answer: "Dense natural quartzites like Taj Mahal offer the organic elegance of marble with zero acid etching or moisture staining. Engineered quartz provides non-porous resilience with zero maintenance, while sealed honed marble delivers unmatched patina for homeowners comfortable with periodic conditioning."
}, {
  id: "item-4",
  question: "Can we relocate plumbing walls, toilets, or slab penetrations?",
  answer: "Yes. Our trade specialists regularly reconfigure wet walls to open sightlines, replace builder-grade fiberglass enclosures with curbless walk-in showers, and relocate drain penetrations through both wood subfloors and concrete foundations safely according to Georgia residential building codes."
}];
export default function Questions() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 15
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0.01 : 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="frequently-asked-questions" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="container mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Narrative Column */}
          <div className="lg:col-span-5 flex flex-col items-start space-y-6">
            <div className="inline-flex items-center gap-2">
              <Badge variant="outline" className="bg-primary/10 border-primary/30 text-primary-foreground font-semibold px-3 py-1 text-xs tracking-wider uppercase rounded-full">
                Planning & Clarity
              </Badge>
            </div>

            <div className="space-y-4">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold tracking-tight text-foreground leading-tight">
                Clear answers for your bathroom remodel
              </h2>
              <div className="h-[1px] w-20 bg-primary/40 my-3" />
              <p className="text-base sm:text-lg text-foreground/80 leading-relaxed font-sans max-w-lg">
                Plan your project with predictable timelines, verified
                waterproofing standards, and transparent guidance tailored to
                Brookhaven homes.
              </p>
            </div>

            {/* Consultation Card */}
            <div className="w-full mt-4 p-6 sm:p-7 rounded-2xl bg-card border border-border shadow-sm">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-primary/15 text-primary shrink-0">
                  <HelpCircle className="size-6 text-foreground" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-base font-semibold text-foreground font-sans">
                    Have a specific question about your layout?
                  </h3>
                  <p className="text-sm text-foreground/75 leading-relaxed font-sans">
                    Speak directly with our Brookhaven project directors about
                    permits, structural considerations, and design choices.
                  </p>
                </div>
              </div>

              <div className="mt-6 pt-5 border-t border-border flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-5 py-2.5 shadow-sm font-medium">
                  <Link to="/ContactRequestAnEstimatePage" className="inline-flex items-center justify-center gap-2">
                    <span>Request an estimate</span>
                    <ArrowUpRight className="size-4" />
                  </Link>
                </Button>

                <a href="tel:+14708479076" className="inline-flex items-center justify-center gap-2 text-sm font-medium text-foreground/90 hover:text-primary transition-colors py-2 px-3">
                  <PhoneCall className="size-4 text-primary" />
                  <span>(470) 847-9076</span>
                </a>
              </div>
            </div>
          </div>

          {/* Right Accordion Column */}
          <div className="lg:col-span-7 bg-card rounded-2xl border border-border shadow-sm p-6 sm:p-8 lg:p-10">
            <Accordion type="single" collapsible defaultValue="item-1" className="w-full divide-y divide-border/80">
              {faqData.map((item, i) => <div data-index={i} key={item.id} className="w-full">
                  <AccordionItem value={item.id} className="border-b-0 py-2 sm:py-3 transition-colors">
                    <AccordionTrigger className="text-left font-sans text-base sm:text-lg font-semibold text-foreground hover:text-primary transition-colors py-4 [&[data-state=open]]:text-foreground">
                      <span className="pr-4">{item.question}</span>
                    </AccordionTrigger>
                    <AccordionContent className="font-sans text-foreground/80 leading-relaxed text-sm sm:text-base pt-1 pb-5 pr-6">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                </div>)}
            </Accordion>
          </div>
        </motion.div>
      </div>
    </section>;
}